import { useMemo, useState } from 'react';
import Button from '../../../shared/components/Button';
import { usePlatformSummary } from '../hooks/usePlatformSummary';
import type { PlatformSummaryQuery } from '../types';
import {
  fmtCount,
  fmtMinor,
  isRangeInvalid,
  statusPercent,
  topMerchantsByGmv,
} from '../summary';

function StatTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <p className="mt-2 text-[24px] font-semibold text-foreground tabular-nums">{value}</p>
      {sub && <p className="mt-1 text-[12px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** Proportional bar for the merchant onboarding-status breakdown. */
function StatusBar({
  label,
  count,
  total,
  tone,
}: {
  label: string;
  count: number;
  total: number;
  tone: string;
}) {
  const pct = statusPercent(count, total);
  return (
    <div>
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {fmtCount.format(count)} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * TCS-admin platform reporting & analytics dashboard (PE-2108, backend PE-2091).
 * Reads the single `/reporting/platform-summary` aggregate.
 */
export function PlatformReportingPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Only the applied window hits the API — typing in a date field shouldn't refetch.
  const [applied, setApplied] = useState<PlatformSummaryQuery>({});

  const { data, isLoading, error, isFetching } = usePlatformSummary(applied);

  const rangeInvalid = useMemo(() => isRangeInvalid(from, to), [from, to]);

  const apply = () => {
    if (rangeInvalid) return;
    setApplied({ from: from || undefined, to: to || undefined });
  };

  const reset = () => {
    setFrom('');
    setTo('');
    setApplied({});
  };

  const topMerchants = useMemo(
    () => topMerchantsByGmv(data?.orders.perMerchant ?? []),
    [data],
  );

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Platform reporting</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Merchants, order volume and platform finance across the whole estate.
        </p>
      </div>

      {/* ---- Date window ---- */}
      <div className="bg-card rounded-2xl border border-border p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
          <Button className="h-[40px]" onClick={apply} disabled={rangeInvalid} loading={isFetching}>
            Apply
          </Button>
          {(applied.from || applied.to) && (
            <Button variant="outline" className="h-[40px]" onClick={reset}>
              Clear
            </Button>
          )}
          {rangeInvalid && (
            <span className="text-[12px] text-red-500">“From” must be on or before “To”.</span>
          )}
        </div>
        <p className="mt-3 text-[12px] text-muted-foreground">
          Both bounds are inclusive. Leave blank for all time.
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading platform summary…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">
          Failed to load the platform summary. This aggregate fans out to four services and has no
          per-leg fallback, so any one of them failing fails the whole call — including with the
          upstream&apos;s own status, not necessarily a 5xx.
        </div>
      ) : !data ? null : (
        <div className="space-y-6">
          {/* ---- Headline tiles ---- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatTile
              label="Merchants"
              value={fmtCount.format(data.merchants.total)}
              sub={`${fmtCount.format(data.merchants.activeCount)} active · ${fmtCount.format(
                data.merchants.suspendedCount,
              )} suspended`}
            />
            <StatTile
              label="Orders"
              value={fmtCount.format(data.orders.totalOrderCount)}
              sub="Excludes cancelled, refunded and transfer-failed"
            />
            <StatTile
              label="Revenue"
              value={fmtMinor(data.orders.totalRevenue, data.orders.currency)}
              sub="Gross merchandise value"
            />
            <StatTile
              label="Commission collected"
              value={fmtMinor(data.finance.commissionCollectedMinor, data.finance.currency)}
              sub={`${fmtMinor(data.finance.platformFeesInvoicedMinor, data.finance.currency)} platform fees invoiced`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ---- Onboarding funnel ---- */}
            <section className="bg-card rounded-2xl border border-border p-5">
              <h2 className="text-[15px] font-semibold text-foreground">Merchant onboarding</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                Where merchants sit in the approval pipeline.
              </p>
              <div className="mt-4 space-y-3">
                <StatusBar
                  label="Active"
                  count={data.merchants.byStatus.active}
                  total={data.merchants.total}
                  tone="bg-emerald-500"
                />
                <StatusBar
                  label="Pending approval"
                  count={data.merchants.byStatus.pendingApproval}
                  total={data.merchants.total}
                  tone="bg-amber-500"
                />
                <StatusBar
                  label="Submitted"
                  count={data.merchants.byStatus.submitted}
                  total={data.merchants.total}
                  tone="bg-blue-500"
                />
                <StatusBar
                  label="Rejected"
                  count={data.merchants.byStatus.rejected}
                  total={data.merchants.total}
                  tone="bg-red-500"
                />
              </div>
            </section>

            {/* ---- Finance ---- */}
            <section className="bg-card rounded-2xl border border-border p-5">
              <h2 className="text-[15px] font-semibold text-foreground">Platform finance</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                What TCS earned versus what it billed, over the selected window.
              </p>
              <dl className="mt-4 divide-y divide-border">
                <div className="flex items-center justify-between py-3">
                  <dt className="text-sm text-muted-foreground">Commission collected</dt>
                  <dd className="text-sm font-medium text-foreground tabular-nums">
                    {fmtMinor(data.finance.commissionCollectedMinor, data.finance.currency)}
                  </dd>
                </div>
                <div className="flex items-center justify-between py-3">
                  <dt className="text-sm text-muted-foreground">Platform fees invoiced</dt>
                  <dd className="text-sm font-medium text-foreground tabular-nums">
                    {fmtMinor(data.finance.platformFeesInvoicedMinor, data.finance.currency)}
                  </dd>
                </div>
                <div className="flex items-center justify-between py-3">
                  <dt className="text-sm text-muted-foreground">Currency</dt>
                  <dd className="text-sm font-medium text-foreground">{data.finance.currency}</dd>
                </div>
              </dl>
            </section>
          </div>

          {/* ---- Per-merchant ---- */}
          <section className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-[15px] font-semibold text-foreground">Top merchants by GMV</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                The ten highest-grossing merchants in the window.
              </p>
            </div>
            {topMerchants.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                No order activity in this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-border bg-muted text-left">
                      <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                      <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Orders</th>
                      <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">GMV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topMerchants.map((m) => (
                      <tr key={m.merchantId} className="hover:bg-muted transition-colors">
                        {/* The aggregate returns ids only — no name join upstream. */}
                        <td className="px-6 py-4 text-sm font-mono text-foreground">{m.merchantId}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground tabular-nums">
                          {fmtCount.format(m.orderCount)}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground tabular-nums">
                          {fmtMinor(m.gmv, data.orders.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
